import { get } from 'es-toolkit/compat';

import { canRefreshParquetUrl } from 'utils/parquetWorker/requestError';
import { ParquetMetadataReadRequest } from 'utils/parquetWorker/types';
import queryClient from 'utils/queryClient';

const maxUrlRefreshes = 3;

export interface ParquetUrlRecoveryState {
  urls: ParquetMetadataReadRequest;
  refreshes: number;
  readers: number;
  pendingRefresh?: Promise<ParquetMetadataReadRequest>;
  signingController?: AbortController;
}

export const createParquetUrlRecoveryState = (urls: ParquetMetadataReadRequest): ParquetUrlRecoveryState => ({
  urls,
  refreshes: 0,
  readers: 0,
});

const findUrlPath = (value: unknown, url: string, path: string[] = []): string[] | undefined => {
  if (value === url) return path;
  if (!value || typeof value !== 'object') return undefined;
  for (const [key, child] of Object.entries(value)) {
    const found = findUrlPath(child, url, [...path, key]);
    if (found) return found;
  }
  return undefined;
};

const findUrlSource = (url: string) => {
  // Use only the original signing query, preserving its scope, permissions and concurrency limit.
  for (const query of queryClient.getQueryCache().getAll()) {
    if (!['getPreSignedExperimentResultPresentationInfo', 'presigdUrl'].includes(String(query.queryKey[0]))) continue;
    const path = findUrlPath(query.state.data, url);
    const queryFn = query.options.queryFn;
    if (!path || typeof queryFn !== 'function') continue;
    return async (signal?: AbortSignal): Promise<ParquetMetadataReadRequest> => {
      // Do not replace presentation/query keys here: doing so would reset the retry budget on every signature.
      const refreshed = await queryFn({ queryKey: query.queryKey, signal, meta: query.meta });
      const parquetFileUrl = path.length ? get(refreshed, path) : refreshed;
      const headUrl =
        path.at(-1) === 'presigned_get_url' ? get(refreshed, [...path.slice(0, -1), 'presigned_head_url']) : undefined;
      if (typeof parquetFileUrl !== 'string' || !/^https?:\/\//.test(parquetFileUrl)) {
        throw new Error('Parquet URL refresh did not return a signed URL');
      }
      return { parquetFileUrl, headUrl: typeof headUrl === 'string' ? headUrl : undefined };
    };
  }
  return undefined;
};

const assertNotAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new DOMException('Parquet loading was aborted', 'AbortError');
};

/** Metadata prefetch and activation can share one three-refresh budget, including failed signing requests. */
export const readWithParquetUrlRecovery = async <Result>(
  urls: ParquetMetadataReadRequest,
  read: (urls: ParquetMetadataReadRequest) => Promise<Result>,
  signal?: AbortSignal,
  state = createParquetUrlRecoveryState(urls)
): Promise<Result> => {
  assertNotAborted(signal);
  state.readers += 1;
  let released = false;
  const releaseReader = () => {
    if (released) return;
    released = true;
    state.readers -= 1;
    if (state.readers === 0) state.signingController?.abort();
  };
  signal?.addEventListener('abort', releaseReader, { once: true });
  try {
    let requestedUrls = state.urls;
    let lastError: unknown;
    try {
      return await read(requestedUrls);
    } catch (error) {
      if (!canRefreshParquetUrl(error)) throw error;
      lastError = error;
    }
    const refresh = findUrlSource(urls.parquetFileUrl);
    if (!refresh) throw lastError;
    while (state.pendingRefresh || state.refreshes < maxUrlRefreshes || requestedUrls !== state.urls) {
      assertNotAborted(signal);
      if (requestedUrls === state.urls) {
        if (!state.pendingRefresh) {
          state.refreshes += 1;
          state.signingController = new AbortController();
          state.pendingRefresh = refresh(state.signingController.signal);
        }
        const pendingRefresh = state.pendingRefresh;
        try {
          state.urls = await pendingRefresh;
        } catch (error) {
          assertNotAborted(signal);
          if (error instanceof Error && error.name === 'AbortError') throw error;
          lastError = error;
          continue;
        } finally {
          if (state.pendingRefresh === pendingRefresh) {
            state.pendingRefresh = undefined;
            state.signingController = undefined;
          }
        }
      }
      assertNotAborted(signal);
      requestedUrls = state.urls;
      try {
        return await read(requestedUrls);
      } catch (error) {
        if (!canRefreshParquetUrl(error)) throw error;
        lastError = error;
      }
    }
    throw lastError;
  } finally {
    signal?.removeEventListener('abort', releaseReader);
    releaseReader();
  }
};
